'use client';

import { useActivityStore } from '@/service/state/Activity/useActivityStore';
import { TripshockLogo } from '@/ui/common/Icons/TripshockLogo';
import { BrandLoader } from '@/ui/common/Loaders/BrandLoader';
import { ReviewCard } from './ReviewCard';
import { FormButton } from '@/ui/common/Buttons/FormButton';
import { InlineLoader } from '@/ui/common/Loaders/InlineLoader';
import { scrollToId } from '@/service/utils/scrollToId';

type TReviewsWidgetProps = {
    activityName: string;
};

export const ReviewsWidget = ({ activityName }: TReviewsWidgetProps) => {
    const showReviews = useActivityStore((state) => state.showReviews);
    const reviews = useActivityStore((state) => state.reviews);
    const noMoreReviews = useActivityStore((state) => state.noMoreReviews);
    const reviewsLoading = useActivityStore((state) => state.reviewsLoading);
    const loadMoreReviews = useActivityStore((state) => state.loadMoreReviews);

    return (
        <div>
            <div id="reviews"></div>
            {showReviews ? (
                <>
                    {!reviews.length && <BrandLoader />}
                    {!!reviews.length && (
                        <>
                            <div className="tx-heading-2 mb-5 flex justify-between text-gray-1 dark:text-white md:mb-10">
                                <h2 data-test-id="reviews-title" className="hidden md:block">
                                    Reviews of {activityName}
                                </h2>
                                <div className="tx-large-regular flex w-full flex-col items-center rounded-2xl bg-blue-9 px-5 py-1 text-center text-gray-2 dark:bg-dark-1 dark:text-white md:w-fit">
                                    Verified traveler reviews provided by
                                    <TripshockLogo className="[&>*:nth-child(2)]:fill-black [&>*:nth-child(2)]:dark:fill-white" />
                                </div>
                            </div>
                            <div className="mx-4 grid gap-3 md:mx-0 md:grid-cols-2 md:gap-5">
                                {reviews.map((item) => (
                                    <ReviewCard item={item} key={item.id} />
                                ))}
                            </div>
                            {!noMoreReviews && (
                                <>
                                    {reviewsLoading ? (
                                        <div className="mt-7 flex h-[50px] w-full items-center justify-center md:mt-10">
                                            <InlineLoader />
                                        </div>
                                    ) : (
                                        <div className="mx-4 mt-4 flex items-center justify-center md:mx-0 md:mt-5">
                                            {reviews.length > 4 && (
                                                <FormButton
                                                    variant="outlined"
                                                    label="Hide"
                                                    onClick={() => {
                                                        scrollToId('reviews', 'center');
                                                        loadMoreReviews(false);
                                                    }}
                                                    className="mx-0 mr-2 h-[50px] max-w-full md:mr-5 md:max-w-[280px]"
                                                    data-test-id="hide-reviews-btn"
                                                />
                                            )}
                                            <FormButton
                                                label="View More"
                                                onClick={() => loadMoreReviews(true)}
                                                className="mx-0 max-w-full md:max-w-[280px]"
                                                data-test-id="more-reviews-btn"
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            ) : (
                <></>
            )}
        </div>
    );
};
